import { getAttStatus } from './get-att-status';

const isCookiesEnabled = () => {
  // Set a test cookie
  document.cookie = 'testcookie=1; SameSite=Lax';

  // Check if the cookie was set
  const enabled = document.cookie.indexOf('testcookie=') !== -1;

  // Delete the test cookie
  document.cookie =
    'testcookie=; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax';

  return enabled;
};

const isLocalStorageAvailable = () => {
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

const getCookieValue = (cookieName: string) => {
  let cookieValue;

  const match = document.cookie.match(new RegExp(`(^| )${cookieName}=([^;]+)`));

  if (match) {
    const cookieValueIndex = 2;
    cookieValue = match[cookieValueIndex];
    cookieValue = cookieValue.split(',');
  }

  return cookieValue;
};

function getDomainFromHost(host: string = window.location.host): string {
  // add cases to setOnSubdomain where we want to set cookies on subdomains and not on the domain
  // for example, for preview links, we want to set cookies on the preview subdomain
  const setOnSubDomainCases = ['netlify.app', 'amplifyapp.com'];

  const specialTLDcases = [
    'com.br',
    'co.uk',
    'com.hk',
    'com.sg',
    'com.my',
    'com.mm',
    'com.kh',
    'com.cn'
    // we are adding this for netlify subdomains,
    // because we don't want cookies to be set on the main domain fro netlify links
  ];
  const parts = host.split('.');

  if (parts.length <= 2) {
    return host; // already a root domain
  }

  const lastTwoParts = parts.slice(-2).join('.');

  if (setOnSubDomainCases.includes(lastTwoParts)) {
    return host;
  }

  if (specialTLDcases.includes(lastTwoParts)) {
    return parts.slice(-3).join('.');
  }

  return lastTwoParts;
}

const setBrowserCookie = (
  cookieName: string,
  value: string | number | boolean | { [key: string]: boolean },
  session?: boolean
) => {
  const now = new Date();
  const { host } = window.location;

  const expiresOn = new Date(now);
  expiresOn.setDate(now.getDate() + 365);
  if (isLocalStorageAvailable()) {
    localStorage.setItem(`${cookieName}_created_date`, now.toString());
  }

  const expireDate = session ? 0 : expiresOn.toUTCString();
  // evaluates to hostname with starting `www.` removed
  const domain = getDomainFromHost(host);

  // Domain = 'localhost' does not work with document.cookie method,
  // in this case do not set the Domain attr.
  if (host.split('.').length === 1) {
    // no "." in a domain - it's localhost or something similar
    document.cookie = `${cookieName}=${value}; expires=${expireDate}; path=/; SameSite=Lax;`;
    return;
  }

  // condition for shopify dev stores, it is not possible to set cookies for the main domain myshopify.com
  // so we set the cookie without the domain attribute for dev stores and testing purposes
  if (domain === 'myshopify.com') {
    document.cookie = `${cookieName}=${value}; expires=${expireDate}; path=/; SameSite=Lax;`;
  } else {
    document.cookie = `${cookieName}=${value}; expires=${expireDate}; path=/; SameSite=Lax; domain=${domain};`;
  }
};

const deleteCookie = (cookieName: string) => {
  const { host } = window.location;
  // get main domain, without subdomain
  const domain = getDomainFromHost(host);

  document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; SameSite=Lax;`;
  // if we are on a subdomain
  if (host !== domain) {
    // delete cookie that matches on subdomain
    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; SameSite=Lax; domain=${host}`;
  }
  // delete cookie that matches on main domain
  document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; SameSite=Lax; domain=${domain}`;
};

const checkConsentGiven = () => {
  const consentValues = getCookieValue('cbg_ccp');
  const cbgCookieValues =
    consentValues &&
    consentValues.map((consent) => ({
      type: consent,
      value: true
    }));

  return {
    consentValues,
    noConsentIsGiven: !consentValues,
    cbgCookieValues
  };
};

// if there is a cbg_ccp cookie set on the browser the user already interacted with the cookie form
const userGaveConsent = () => {
  const consentValues = getCookieValue('cbg_ccp');
  return !!consentValues;
};

const userConfirmedAge = () => {
  const ageCookie = getCookieValue('cbgAge');
  return !!ageCookie;
};

const removeOptionalConsentForDeniedAtt = () => {
  const consentValues = getCookieValue('cbg_ccp');

  if (getAttStatus() !== 'denied' || !consentValues) {
    return false;
  }

  const sanitizedConsentValues = consentValues
    .map((consent) => consent.trim())
    .filter((consent) => !['marketing', 'performance'].includes(consent));

  if (sanitizedConsentValues.length === consentValues.length) {
    return false;
  }

  setBrowserCookie(
    'cbg_ccp',
    sanitizedConsentValues.length > 0
      ? sanitizedConsentValues.join(',')
      : 'necessary'
  );

  return true;
};

const isCookiesAllowed = () => {
  const consentValues = getCookieValue('cbg_ccp');
  return (
    !!consentValues &&
    !(consentValues?.length === 1 && consentValues[0] === 'necessary')
  );
};

export {
  isCookiesEnabled,
  getCookieValue,
  setBrowserCookie,
  deleteCookie,
  checkConsentGiven,
  userGaveConsent,
  getDomainFromHost,
  isCookiesAllowed,
  userConfirmedAge,
  isLocalStorageAvailable,
  removeOptionalConsentForDeniedAtt
};
